import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {styled, UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DropdownSeparator} from '../../../common/dropdowns/dropdownSeparator';
import {PermissionSimpleDropdownItem} from '../../../common/dropdowns/permissionSimpleDropdownItem';
import {EditSidebarCopy} from '../editor/copy/edit-sidebar-copy';
import {HideInSidebarCopy} from '../editor/copy/hide-in-sidebar-copy';
import {WorkspaceLineMoreButton} from '../line/workspaceLineMoreButton';

/*
 * Props.
 */

export interface WorkspaceHeaderMoreProps {
  /** Is the app currently offline. */
  isOffline?: boolean;
  /** Handler called onHide. If undefined hide option will not be rendered. */
  onHide?: Handler;
  /** Handler called onEditWorkspace */
  onEditWorkspaces: Handler;
}

/*
 * Style.
 */

const StyledIconContainer = styled.div`
  padding: 3px;
`;

/*
 * Component.
 */

export const WorkspaceHeaderMore: FC<WorkspaceHeaderMoreProps> = (props) => {
  const theme = useUserTheme();

  if (props.isOffline) {
    return renderOfflineIcon(theme);
  }

  const renderMenu = (popoverProps: PopoverTransferProps) =>
    renderMoreDropdown(popoverProps, props.onEditWorkspaces, props.onHide);

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => <WorkspaceLineMoreButton isMoreOpen={isOpen} theme={theme} />}
      renderModal={renderMenu}
    />
  );
};

/*
 * Helpers.
 */

function renderOfflineIcon(theme: UserTheme) {
  return (
    <TooltipCoordinator render={renderOfflineTooltip}>
      <StyledIconContainer>
        <Icon name="offlineFilledSmall" color={theme.headerMore} />
      </StyledIconContainer>
    </TooltipCoordinator>
  );
}

function renderOfflineTooltip() {
  return <Trans id="workspace-header-offline-tooltip">Not available while offline</Trans>;
}

function renderMoreDropdown(popoverProps: PopoverTransferProps, onEditWorkspace: Handler, onHide?: Handler) {
  return (
    <SimpleDropdown {...popoverProps}>
      {onHide && (
        <>
          <PermissionSimpleDropdownItem onSelect={onHide}>
            <HideInSidebarCopy />
          </PermissionSimpleDropdownItem>
          <DropdownSeparator />
        </>
      )}
      <PermissionSimpleDropdownItem onSelect={onEditWorkspace}>
        <EditSidebarCopy />
      </PermissionSimpleDropdownItem>
    </SimpleDropdown>
  );
}
